// Membership Management Component
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  MoreVert,
  CardMembership,
  Refresh,
} from "@mui/icons-material";
import apiService from "../../services/api";
import authService from "../../services/auth";
import { useCurrency } from "../../contexts/CurrencyContext";
import { STATUS_OPTIONS } from "../../constants";

const MembershipManagement = () => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMembership, setEditingMembership] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);

  const { currencySymbol, formatPrice } = useCurrency();

  const isAdminLike = (user) => String(user?.role_name || "").toLowerCase().includes("admin");

  const hasPermission = (module, action = "view") => {
    const user = authService.getUser();
    if (isAdminLike(user)) return true;
    if (!userPermissions) return false;
    return userPermissions?.[module]?.[action] === true;
  };

  const fetchPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.id) return;
      const response = await apiService.get(`/roles/user/${user.id}/permissions`);
      setUserPermissions(response.data || {});
    } catch (e) {
      // safest fallback: no permissions
      setUserPermissions({});
    }
  };

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    duration_type: "monthly",
    duration_months: 1,
    price: "",
    discount_percentage: "",
    status: STATUS_OPTIONS.ACTIVE,
  });

  const DURATION_OPTIONS = [
    { value: 'monthly', label: 'Monthly', months: 1 },
    { value: 'quarterly', label: 'Quarterly', months: 3 },
    { value: 'half_yearly', label: 'Half Yearly', months: 6 },
    { value: 'annually', label: 'Annually', months: 12 },
    { value: 'lifetime', label: 'Life Long', months: null },
  ];

  useEffect(() => {
    fetchMemberships();
    fetchPermissions();
  }, []);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const response = await apiService.get("/memberships");
      if (response.success) {
        setMemberships(response.data.memberships || []);
      }
    } catch (err) {
      setError("Error fetching memberships: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const filteredMemberships = (memberships || []).filter((membership) => {
    const matchesSearch = membership.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || membership.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Auto-update duration_months when duration_type changes
    if (name === 'duration_type') {
      const selectedDuration = DURATION_OPTIONS.find(opt => opt.value === value);
      if (selectedDuration) {
        setFormData((prev) => ({
          ...prev,
          duration_type: value,
          duration_months: selectedDuration.months === null ? 0 : selectedDuration.months,
        }));
      }
    }
    
    setDialogError('');
  };

  const handleOpenDialog = (membership = null) => {
    // Permission checks (UI-level). API also enforces this.
    if (membership && !hasPermission("memberships", "edit")) {
      setError("Permission denied");
      return;
    }
    if (!membership && !hasPermission("memberships", "add")) {
      setError("Permission denied");
      return;
    }

    if (membership) {
      setEditingMembership(membership);
      setFormData({
        name: membership.name || "",
        description: membership.description || "",
        duration_type: membership.duration_type || "monthly",
        duration_months: membership.duration_months || 1,
        price: membership.price || "",
        discount_percentage: membership.discount_percentage !== null && membership.discount_percentage !== undefined ? membership.discount_percentage : "",
        status: membership.status || STATUS_OPTIONS.ACTIVE,
      });
    } else {
      setEditingMembership(null);
      setFormData({
        name: "",
        description: "",
        duration_type: "monthly",
        duration_months: 1,
        price: "",
        discount_percentage: "",
        status: STATUS_OPTIONS.ACTIVE,
      });
    }
    setDialogError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMembership(null);
    setFormData({
      name: "",
      description: "",
      duration_type: "monthly",
      duration_months: 1,
      price: "",
      discount_percentage: "",
      status: STATUS_OPTIONS.ACTIVE,
    });
    setDialogError('');
  };

  const handleSubmit = async () => {
    try {
      setDialogError('');

      // Permission checks (UI-level). API also enforces this.
      if (editingMembership && !hasPermission("memberships", "edit")) {
        setDialogError("Permission denied");
        return;
      }
      if (!editingMembership && !hasPermission("memberships", "add")) {
        setDialogError("Permission denied");
        return;
      }
      
      // Validation
      if (!formData.name.trim()) {
        setDialogError('Name is required');
        return;
      }
      if (!formData.price || parseFloat(formData.price) < 0) {
        setDialogError('Valid price is required');
        return;
      }
      // Allow 0 for discount percentage, but validate it's a valid number between 0 and 100
      const discountValue = formData.discount_percentage === '' || formData.discount_percentage === null || formData.discount_percentage === undefined 
        ? 0 
        : parseFloat(formData.discount_percentage);
      
      if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
        setDialogError('Discount percentage must be between 0 and 100');
        return;
      }

      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        discount_percentage: discountValue, // Always a number (can be 0)
        duration_months: formData.duration_type === 'lifetime' ? null : parseInt(formData.duration_months),
      };

      if (editingMembership) {
        await apiService.put(`/memberships/${editingMembership.id}`, payload);
        setSuccessMessage('Membership updated successfully!');
      } else {
        await apiService.post("/memberships", payload);
        setSuccessMessage('Membership created successfully!');
      }

      handleCloseDialog();
      fetchMemberships();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setDialogError(err.response?.data?.error || err.message || 'Failed to save membership');
    }
  };

  const handleMenuOpen = (event, membership) => {
    setAnchorEl(event.currentTarget);
    setSelectedMembership(membership);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMembership(null);
  };

  const handleDelete = async () => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("memberships", "delete")) {
      setError("Permission denied");
      handleMenuClose();
      return;
    }

    if (!selectedMembership) return;
    
    try {
      await apiService.delete(`/memberships/${selectedMembership.id}`);
      setSuccessMessage('Membership deleted successfully!');
      fetchMemberships();
      handleMenuClose();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete membership');
      handleMenuClose();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Membership Plans
          </Typography>

          {hasPermission("memberships", "add") && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Membership
          </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              placeholder="Search memberships..."
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={fetchMemberships} title="Refresh">
              <Refresh />
            </IconButton>
          </Box>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Discount %</TableCell>
                <TableCell>Active Members</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredMemberships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No memberships found
                  </TableCell>
                </TableRow>
              ) : (
                filteredMemberships.map((membership) => (
                  <TableRow key={membership.id}>
                    <TableCell>{membership.name}</TableCell>
                    <TableCell>
                      {membership.duration_type === 'lifetime' 
                        ? 'Life Long' 
                        : (DURATION_OPTIONS.find(opt => opt.value === membership.duration_type)?.label || membership.duration_type)}
                    </TableCell>
                    <TableCell>{formatPrice(membership.price)}</TableCell>
                    <TableCell>{membership.discount_percentage}%</TableCell>
                    <TableCell>{membership.active_members || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={membership.status}
                        color={membership.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {(hasPermission("memberships", "edit") || hasPermission("memberships", "delete")) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, membership)}
                        >
                          <MoreVert />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {hasPermission("memberships", "edit") && (
            <MenuItem onClick={() => {
              handleOpenDialog(selectedMembership);
              handleMenuClose();
            }}>
              <Edit sx={{ mr: 1 }} /> Edit
            </MenuItem>
          )}
          {hasPermission("memberships", "delete") && (
            <MenuItem onClick={handleDelete}>
              <Delete sx={{ mr: 1 }} /> Delete
            </MenuItem>
          )}
        </Menu>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingMembership ? "Edit Membership" : "Add Membership"}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            <Box sx={{ pt: 2 }}>
              {/* Row 1: Name (full width) */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Box>

              {/* Row 2: Duration Type + Duration Months (two in a row) */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Duration Type</InputLabel>
                  <Select
                    name="duration_type"
                    value={formData.duration_type}
                    onChange={handleInputChange}
                    label="Duration Type"
                  >
                    {DURATION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Duration (Months)"
                  name="duration_months"
                  type="number"
                  value={formData.duration_type === 'lifetime' ? '' : formData.duration_months}
                  onChange={handleInputChange}
                  disabled
                  placeholder={formData.duration_type === 'lifetime' ? 'N/A (Lifetime)' : ''}
                />
              </Box>

              {/* Row 3: Price + Discount Percentage (two in a row) */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currencySymbol}
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Discount Percentage"
                  name="discount_percentage"
                  type="number"
                  value={formData.discount_percentage}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  required
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                />
              </Box>

              {/* Row 4: Status (half width) + empty space */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ width: "100%" }}></Box>
              </Box>

              {/* Row 5: Description (full width) */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe this membership plan..."
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingMembership ? "Update" : "Add"} Membership
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default MembershipManagement;

